import { useMemo } from 'react';
import { type ProductField, type ProductCombination, type SelectedOptions } from '../type';

export const useCombinations = (
  fields: ProductField[],
  selectedOptions: SelectedOptions,
  prices: Record<string, number>
): ProductCombination[] => {
  return useMemo(() => {
    // Get all selected option values for each field
    const selectedFieldOptions: Record<number, Array<{ id: string; value: string }>> = {};
    
    fields.forEach(field => {
      const selected = selectedOptions[field.id] || [];
      selectedFieldOptions[field.id] = field.options.filter(option => 
        selected.includes(option.id)
      );
    });

    // Filter out fields with no selections
    const fieldsWithSelections = fields.filter(field => 
      selectedFieldOptions[field.id] && selectedFieldOptions[field.id].length > 0
    );

    if (fieldsWithSelections.length === 0) {
      return [];
    }

    // Generate all combinations
    const generateCombinations = (
      currentFieldIndex: number,
      currentCombination: Record<string, string>
    ): ProductCombination[] => {
      if (currentFieldIndex >= fieldsWithSelections.length) {
        const combinationId = Object.values(currentCombination).join('-');
        return [{
          id: combinationId,
          combination: { ...currentCombination },
          price: prices[combinationId] || 0
        }];
      }

      const field = fieldsWithSelections[currentFieldIndex];
      const options = selectedFieldOptions[field.id];
      const combinations: ProductCombination[] = [];

      options.forEach(option => {
        const newCombination = {
          ...currentCombination,
          [field.label.toLowerCase().replace(/\s+/g, '')]: option.value
        };
        
        combinations.push(...generateCombinations(currentFieldIndex + 1, newCombination));
      });

      return combinations;
    };

    return generateCombinations(0, {});
  }, [fields, selectedOptions, prices]);
};